import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiService } from "../lib/api";
import {
  MONTH_NAMES,
  formatPeriodLabel,
  getMonthOptions,
  getQuarterOptions,
  getYearOptions,
  readPeriodParam,
  setPeriodParam,
  withPeriod,
} from "../lib/periods";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Progress } from "../components/ui/progress";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "../components/ui/tooltip";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { ScrollArea } from "../components/ui/scroll-area";
import { 
  Loader2, TrendingUp, TrendingDown, DollarSign, Calendar, AlertTriangle, 
  CheckCircle, Clock, Users, FileText, Globe, Shield, BarChart3, 
  Wallet, Target, AlertCircle, ChevronRight, X, Flame, Building2,
  PieChartIcon, TableIcon, LineChart as LineChartIcon, ExternalLink, ChevronDown
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area, LineChart, Line, Legend
} from "recharts";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";

// Projection horizon, derived the same way as on MetricDetailPage: take the
// backend's runway_months, round it up and add one month of headroom so the
// line runs past the end of the runway. Capped at 36 months and never below
// 1, so a missing or odd runway value can never produce an endless series.
const PROJECTION_MAX_MONTHS = 36;

/**
 * How far the runway projection draws: runway plus a month.
 *
 * Left alone deliberately. This is a CASH curve, and widening it to reach a
 * 2031 loan maturity would extrapolate a burn rate averaged over months
 * ending in September out to five years and draw it as a forecast. Debt
 * obligations belong on their own dated view on the loans page, not bolted
 * onto this one.
 */
const projectionHorizonMonths = (runwayMonths) =>
  Math.max(1, Math.min(PROJECTION_MAX_MONTHS, Math.ceil(runwayMonths || 0) + 1));

function AdminDashboardPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  // The period lives in the query string so it survives navigation.
  const selectedQuarter = readPeriodParam(searchParams);
  const [dismissedAlerts, setDismissedAlerts] = useState([]);
  const [categoryView, setCategoryView] = useState("donut"); // donut, table, line
  const [showVendorList, setShowVendorList] = useState(false);
  const [showCustomerList, setShowCustomerList] = useState(false);
  const [periodPopoverOpen, setPeriodPopoverOpen] = useState(false);
  const [chartMonthsToShow, setChartMonthsToShow] = useState(12); // Default to last 12 months

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    try {
      const quarter = selectedQuarter === "all" ? undefined : selectedQuarter;
      const res = await apiService.getDashboardAnalytics(quarter);
      setAnalytics(res.data);
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
    } finally {
      setLoading(false);
    }
  }, [selectedQuarter]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const formatCurrencyShort = (value) => {
    if (value >= 1000000) return `€${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `€${(value / 1000).toFixed(1)}K`;
    return `€${value?.toFixed(0) || 0}`;
  };

  // Monthly keys arrive as a full month name ("January 2026"), but a raw key
  // ("2026-01") can still turn up; render a short label for either shape.
  const formatMonthTick = (value) => {
    if (value === null || value === undefined) return "";
    const raw = String(value);
    const isoMatch = raw.match(/^(\d{4})-(\d{1,2})$/);
    if (isoMatch) {
      const name = MONTH_NAMES[parseInt(isoMatch[2], 10) - 1];
      return name ? `${name.substring(0, 3)} ${isoMatch[1]}` : raw;
    }
    const parts = raw.split(" ");
    return parts[1] ? `${parts[0].substring(0, 3)} ${parts[1]}` : parts[0];
  };

  const currentYear = new Date().getFullYear();

  // One shared option list for every analytics page (see lib/periods.js), so
  // the same periods are offered wherever the user is.
  const quarterOptions = getQuarterOptions();
  const yearOptions = getYearOptions();
  const monthOptions = getMonthOptions();

  // Get display label for selected period
  const getPeriodLabel = () => formatPeriodLabel(selectedQuarter);

  // Handle period selection
  const handlePeriodSelect = (value) => {
    // The query string is the source of truth for the period.
    setSearchParams(setPeriodParam(searchParams, value), { replace: true });
    setPeriodPopoverOpen(false);
  };

  const COLORS = ['#10b981', '#3b82f6', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899', '#84cc16'];

  // Friendly labels for the economic income groups returned in income_breakdown.
  //
  // `financing` is NOT all capital: it holds share contributions, shareholder
  // loans and the director current account together. Calling the total "Capital
  // raised" described 200,000 of borrowed money as money raised, which is the
  // defect the loans register exists to fix. The group keeps its honest name
  // and the split is shown beside it.
  const INCOME_GROUP_LABELS = {
    revenue: "Real revenue",
    financing: "Funding received",
    subsidy: "Subsidies",
    tax_refund: "Tax refunds",
    internal: "Internal transfers",
    fx: "FX conversions",
    operating: "Other inflows",
  };

  const dismissAlert = (index) => {
    setDismissedAlerts(prev => [...prev, index]);
  };

  const activeAlerts = analytics?.alerts?.filter((_, idx) => !dismissedAlerts.includes(idx)) || [];

  const getAlertIcon = (type) => {
    switch (type) {
      case 'danger': return <AlertTriangle className="w-5 h-5 text-red-500" />;
      case 'warning': return <AlertCircle className="w-5 h-5 text-amber-500" />;
      default: return <AlertCircle className="w-5 h-5 text-blue-500" />;
    }
  };

  const getAlertBg = (type) => {
    switch (type) {
      case 'danger': return 'bg-red-50 border-red-200';
      case 'warning': return 'bg-amber-50 border-amber-200';
      default: return 'bg-blue-50 border-blue-200';
    }
  };

  // Prepare chart data
  const categoryData = analytics?.spend_by_category?.slice(0, 8).map((cat, idx) => ({
    name: cat.category.length > 12 ? cat.category.substring(0, 12) + '...' : cat.category,
    value: cat.amount,
    fullName: cat.category,
    color: COLORS[idx % COLORS.length]
  })) || [];

  // Process monthly trend data with display range filtering
  const allTrendData = analytics?.monthly_trend?.map(m => ({
    month: m.month,
    shortMonth: m.month.split(" ")[0].substring(0, 3) + " " + m.month.split(" ")[1]?.substring(2), // "Jan 26" format
    income: m.income,
    expense: m.expense,
    net: m.income - m.expense
  })) || [];
  
  // Apply chart display range
  const trendData = chartMonthsToShow === 0 
    ? allTrendData 
    : allTrendData.slice(-chartMonthsToShow);

  // Fixed vs variable share on the one base shared with ExpensesPage and
  // MetricDetailPage: fixed + variable, guarded against a zero base.
  const fixedExpenses = analytics?.fixed_expenses || 0;
  const variableExpenses = analytics?.variable_expenses || 0;
  const fixedVariableBase = fixedExpenses + variableExpenses;
  const fixedSharePct = fixedVariableBase > 0 ? (fixedExpenses / fixedVariableBase) * 100 : 0;
  const variableSharePct = fixedVariableBase > 0 ? (variableExpenses / fixedVariableBase) * 100 : 0;
  // Expenses without a counterparty name cannot be split into fixed or variable.
  const fixedVariableUnattributed = analytics?.fixed_variable_unattributed || 0;

  const fixedVsVariable = [
    { name: 'Fixed', value: analytics?.fixed_expenses || 0, color: '#3b82f6' },
    { name: 'Variable', value: analytics?.variable_expenses || 0, color: '#8b5cf6' }
  ];

  // Cash flow bridge -> floating-bar waterfall. Each step sits on an invisible
  // "base" offset so the visible bar starts where the previous one ended;
  // opening and closing are full-height reference columns.
  const bridge = analytics?.cash_flow_bridge;
  const waterfallData = (() => {
    if (!bridge) return [];
    const rows = [{ name: "Opening", base: 0, value: bridge.opening_cash, fill: "#64748b", total: bridge.opening_cash, isTotal: true }];
    let running = bridge.opening_cash;
    bridge.steps.forEach((s) => {
      if (s.amount === 0) return;
      const base = s.amount >= 0 ? running : running + s.amount;
      const fill = s.type === "residual" ? "#94a3b8" : s.amount >= 0 ? "#10b981" : "#ef4444";
      running += s.amount;
      rows.push({ name: s.label, base, value: Math.abs(s.amount), fill, delta: s.amount });
    });
    rows.push({ name: "Closing", base: 0, value: bridge.closing_cash, fill: "#3b82f6", total: bridge.closing_cash, isTotal: true });
    return rows;
  })();

  // Runway projection: cash drawn down at the current monthly burn, with a
  // +/-20% scenario band so the "months left" figure has a visible range.
  const runwayProjection = (() => {
    const cash = analytics?.cash_balance || 0;
    const burn = analytics?.burn_rate || 0;
    if (burn <= 0 || cash <= 0) return [];
    const months = projectionHorizonMonths(analytics?.runway_months);
    return Array.from({ length: months + 1 }, (_, m) => ({
      month: m,
      base: Math.max(0, cash - burn * m),
      faster: Math.max(0, cash - burn * 1.2 * m),
      slower: Math.max(0, cash - burn * 0.8 * m),
    }));
  })();

  const monthlyStatement = analytics?.monthly_cash_statement || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <TooltipProvider>
    <div className="space-y-6" data-testid="admin-dashboard-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Executive Dashboard</h1>
          <p className="text-slate-500 mt-1">Real-time financial intelligence</p>
        </div>
        <div className="flex items-center gap-2">
          <Popover open={periodPopoverOpen} onOpenChange={setPeriodPopoverOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-[180px] justify-between" data-testid="period-select">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-slate-400" />
                  <span>{getPeriodLabel()}</span>
                </div>
                <ChevronDown className="w-4 h-4 text-slate-400" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[320px] p-0" align="end">
              <div className="p-3 border-b">
                <h4 className="font-semibold text-sm">Select Time Period</h4>
              </div>
              <div className="p-3 space-y-4">
                {/* Quick Options */}
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-2">Quick Select</p>
                  <div className="grid grid-cols-3 gap-2">
                    <Button 
                      variant={selectedQuarter === "all" ? "default" : "outline"} 
                      size="sm"
                      onClick={() => handlePeriodSelect("all")}
                      className="text-xs"
                    >
                      All Time
                    </Button>
                    <Button 
                      variant={selectedQuarter === `Q${Math.ceil((new Date().getMonth() + 1) / 3)}-${currentYear}` ? "default" : "outline"} 
                      size="sm"
                      onClick={() => handlePeriodSelect(`Q${Math.ceil((new Date().getMonth() + 1) / 3)}-${currentYear}`)}
                      className="text-xs"
                    >
                      This Quarter
                    </Button>
                    <Button 
                      variant={selectedQuarter === `year-${currentYear}` ? "default" : "outline"} 
                      size="sm"
                      onClick={() => handlePeriodSelect(`year-${currentYear}`)}
                      className="text-xs"
                    >
                      This Year
                    </Button>
                  </div>
                </div>
                
                {/* By Year */}
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-2">By Year</p>
                  <div className="grid grid-cols-4 gap-2">
                    {yearOptions.map(y => (
                      <Button 
                        key={y.value}
                        variant={selectedQuarter === y.value ? "default" : "outline"} 
                        size="sm"
                        onClick={() => handlePeriodSelect(y.value)}
                        className="text-xs"
                      >
                        {y.label}
                      </Button>
                    ))}
                  </div>
                </div>
                
                {/* By Month */}
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-2">By Month</p>
                  <ScrollArea className="h-[120px]">
                    <div className="grid grid-cols-3 gap-2 pr-3">
                      {monthOptions.map(m => (
                        <Button 
                          key={m.value}
                          variant={selectedQuarter === m.value ? "default" : "outline"} 
                          size="sm"
                          onClick={() => handlePeriodSelect(m.value)}
                          className="text-xs justify-start"
                        >
                          {m.label}
                        </Button>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
                
                {/* By Quarter */}
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-2">By Quarter</p>
                  <ScrollArea className="h-[180px]">
                    <div className="grid grid-cols-2 gap-2 pr-3">
                      {quarterOptions.map(q => (
                        <Button 
                          key={q.value}
                          variant={selectedQuarter === q.value ? "default" : "outline"} 
                          size="sm"
                          onClick={() => handlePeriodSelect(q.value)}
                          className="text-xs justify-start"
                        >
                          {q.label}
                        </Button>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
                
                {/* Chart Range */}
                <div className="border-t pt-3">
                  <p className="text-xs font-medium text-slate-500 mb-2">Chart Display Range</p>
                  <div className="flex gap-2">
                    {[6, 12, 24, 0].map(months => (
                      <Button 
                        key={months}
                        variant={chartMonthsToShow === months ? "default" : "outline"} 
                        size="sm"
                        onClick={() => setChartMonthsToShow(months)}
                        className="text-xs flex-1"
                      >
                        {months === 0 ? "All" : `${months}mo`}
                      </Button>
                    ))}
                  </div>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Executive Summary - the full picture in one line */}
      <Card className="border-0 shadow-sm bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <CardContent className="p-5">
          <div className="flex items-start justify-between flex-wrap gap-4">
            <div className="max-w-xl">
              <p className="text-xs font-medium text-slate-400 uppercase tracking-wider mb-1">
                Where we stand · {getPeriodLabel()}
                {analytics?.cash_balance_as_of && (
                  <span className="normal-case tracking-normal text-slate-500">
                    {" "}· bank data to {analytics.cash_balance_as_of}
                    {analytics?.cash_balance_complete_to &&
                      analytics.cash_balance_complete_to !== analytics.cash_balance_as_of &&
                      `, and only to ${analytics.cash_balance_complete_to} on every account`}
                  </span>
                )}
              </p>
              <p className="text-sm leading-relaxed text-slate-200">
                Raised <span className="font-semibold text-white">{formatCurrency(analytics?.equity_raised)}</span> in share capital, borrowed{" "}
                <span className="font-semibold text-white">{formatCurrency(analytics?.loans_received)}</span> that has to go back
                {(analytics?.shareholder_account_received || 0) > 0 && (
                  <>, took <span className="font-semibold text-white">{formatCurrency(analytics?.shareholder_account_received)}</span> through the director current account</>
                )}
                {(analytics?.financing_unattributed || 0) > 0 && (
                  <>, plus <span className="font-semibold text-white">{formatCurrency(analytics?.financing_unattributed)}</span> of funding whose kind is not recorded</>
                )}
                , and earned{" "}
                <span className="font-semibold text-white">{formatCurrency(analytics?.real_revenue)}</span> in revenue.
                Spent <span className="font-semibold text-white">{formatCurrency((analytics?.total_expense || 0) - (analytics?.intercompany_transfers?.amount || 0))}</span> on operations
                {analytics?.intercompany_transfers?.amount > 0 && (
                  <> and funded the satellite with <span className="font-semibold text-white">{formatCurrency(analytics?.intercompany_transfers?.amount)}</span></>
                )}.
                {" "}That leaves <span className="font-semibold text-emerald-400">{formatCurrency(analytics?.cash_balance)}</span> in the bank
                {analytics?.runway_months !== null
                  ? <>, about <span className="font-semibold text-white">{analytics?.runway_months} months</span> of runway at the current burn.</>
                  : <>, with no net burn to run down the balance.</>}
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3">
              {[
                { label: "Cash left", value: formatCurrency(analytics?.cash_balance), accent: "text-emerald-400" },
                { label: "Runway", value: analytics?.runway_months === null ? "∞" : `${analytics?.runway_months} mo`, accent: "text-white" },
                { label: "Monthly burn", value: formatCurrency(analytics?.burn_rate), accent: "text-red-400" },
                { label: "Share capital", value: formatCurrency(analytics?.equity_raised), accent: "text-white" },
                { label: "Borrowed", value: formatCurrency(analytics?.loans_received), accent: "text-amber-400" },
                { label: "Revenue", value: formatCurrency(analytics?.real_revenue), accent: "text-white" },
                { label: "Operating cash flow", value: formatCurrency(analytics?.operating_cash_flow), accent: analytics?.operating_cash_flow >= 0 ? "text-emerald-400" : "text-red-400" },
              ].map((s) => (
                <div key={s.label}>
                  <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wider">{s.label}</p>
                  <p className={`text-lg font-bold ${s.accent}`}>{s.value}</p>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* CEO Smart Alerts */}
      {activeAlerts.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            Smart Alerts
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {activeAlerts.map((alert, idx) => (
              <div 
                key={idx} 
                className={`relative p-4 rounded-lg border ${getAlertBg(alert.type)} transition-all hover:shadow-md`}
              >
                <button 
                  onClick={() => dismissAlert(analytics?.alerts?.indexOf(alert))}
                  className="absolute top-2 right-2 text-slate-400 hover:text-slate-600"
                >
                  <X className="w-4 h-4" />
                </button>
                <div className="flex items-start gap-3">
                  {getAlertIcon(alert.type)}
                  <div>
                    <h3 className="font-semibold text-slate-900">{alert.title}</h3>
                    <p className="text-sm text-slate-600 mt-0.5">{alert.message}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Row 1: SURVIVAL */}
      <div>
        <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-3 flex items-center gap-2">
          <Shield className="w-4 h-4 text-emerald-500" />
          Survival Metrics
        </h2>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <Card 
            className="border-0 shadow-sm bg-gradient-to-br from-emerald-50 to-white cursor-pointer hover:shadow-md transition-shadow group"
            onClick={() => navigate(withPeriod("/analytics/cash-balance", selectedQuarter))}
          >
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Cash Balance</p>
                  <p className={`text-2xl font-bold mt-1 ${analytics?.cash_balance >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                    {formatCurrency(analytics?.cash_balance)}
                  </p>
                </div>
                <div className="p-3 rounded-xl bg-emerald-500/10 group-hover:bg-emerald-500/20 transition-colors">
                  <Wallet className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
              <div className="mt-2 flex items-center text-xs text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity">
                <span>View details</span>
                <ExternalLink className="w-3 h-3 ml-1" />
              </div>
            </CardContent>
          </Card>

          <Card 
            className="border-0 shadow-sm bg-gradient-to-br from-red-50 to-white cursor-pointer hover:shadow-md transition-shadow group"
            onClick={() => navigate(withPeriod("/analytics/burn-rate", selectedQuarter))}
          >
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Burn Rate</p>
                  <p className="text-2xl font-bold text-red-500 mt-1">
                    {formatCurrency(analytics?.burn_rate)}<span className="text-sm font-normal">/mo</span>
                  </p>
                  {analytics?.burn_rate_change !== 0 && (
                    <p className={`text-xs mt-1 ${analytics?.burn_rate_change > 0 ? 'text-red-500' : 'text-emerald-500'}`}>
                      {analytics?.burn_rate_change > 0 ? '↑' : '↓'} {Math.abs(analytics?.burn_rate_change)}% vs last month
                    </p>
                  )}
                </div>
                <div className="p-3 rounded-xl bg-red-500/10 group-hover:bg-red-500/20 transition-colors">
                  <Flame className="w-6 h-6 text-red-500" />
                </div>
              </div>
              <div className="mt-2 flex items-center text-xs text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity">
                <span>View details</span>
                <ExternalLink className="w-3 h-3 ml-1" />
              </div>
            </CardContent>
          </Card>

          <Card 
            className="border-0 shadow-sm bg-gradient-to-br from-orange-50 to-white cursor-pointer hover:shadow-md transition-shadow group"
            onClick={() => navigate(withPeriod("/analytics/burn-rate", selectedQuarter))}
          >
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Avg Burn Rate</p>
                  <p className="text-2xl font-bold text-orange-500 mt-1">
                    {formatCurrency(analytics?.avg_burn_rate)}<span className="text-sm font-normal">/mo</span>
                  </p>
                  <p className="text-xs mt-1 text-slate-400">
                    All-time average
                  </p>
                </div>
                <div className="p-3 rounded-xl bg-orange-500/10 group-hover:bg-orange-500/20 transition-colors">
                  <BarChart3 className="w-6 h-6 text-orange-500" />
                </div>
              </div>
              <div className="mt-2 flex items-center text-xs text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity">
                <span>View details</span>
                <ExternalLink className="w-3 h-3 ml-1" />
              </div>
            </CardContent>
          </Card>

          <Card 
            className="border-0 shadow-sm bg-gradient-to-br from-blue-50 to-white cursor-pointer hover:shadow-md transition-shadow group"
            onClick={() => navigate(withPeriod("/analytics/runway", selectedQuarter))}
          >
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Runway</p>
                  <p className={`text-2xl font-bold mt-1 ${
                    analytics?.runway_months === null ? 'text-emerald-600' :
                    analytics?.runway_months < 6 ? 'text-red-500' : 
                    analytics?.runway_months < 12 ? 'text-amber-500' : 'text-emerald-600'
                  }`}>
                    {analytics?.runway_months === null ? '∞' : `${analytics?.runway_months}`}
                    <span className="text-sm font-normal"> months</span>
                  </p>
                </div>
                <div className="p-3 rounded-xl bg-blue-500/10 group-hover:bg-blue-500/20 transition-colors">
                  <Clock className="w-6 h-6 text-blue-600" />
                </div>
              </div>
              <div className="mt-2 flex items-center text-xs text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity">
                <span>View details</span>
                <ExternalLink className="w-3 h-3 ml-1" />
              </div>
            </CardContent>
          </Card>

          <Card 
            className="border-0 shadow-sm bg-gradient-to-br from-violet-50 to-white cursor-pointer hover:shadow-md transition-shadow group"
            onClick={() => navigate(withPeriod("/analytics/net-cash-flow", selectedQuarter))}
          >
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Operating Cash Flow</p>
                  <p className={`text-2xl font-bold mt-1 ${analytics?.operating_cash_flow >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
                    {formatCurrency(analytics?.operating_cash_flow)}
                  </p>
                </div>
                <div className="p-3 rounded-xl bg-violet-500/10 group-hover:bg-violet-500/20 transition-colors">
                  {analytics?.operating_cash_flow >= 0 ?
                    <TrendingUp className="w-6 h-6 text-emerald-600" /> :
                    <TrendingDown className="w-6 h-6 text-red-500" />
                  }
                </div>
              </div>
              <div className="mt-2 flex items-center text-xs text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity">
                <span>View details</span>
                <ExternalLink className="w-3 h-3 ml-1" />
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Row 1.5: CASH FLOW */}
      {bridge && (
        <div>
          <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-3 flex items-center gap-2">
            <Wallet className="w-4 h-4 text-emerald-500" />
            Cash Flow · {getPeriodLabel()}
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Cash flow bridge (waterfall) */}
            <Card className="border-0 shadow-sm lg:col-span-2">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold text-slate-900">Where the money came from and went</CardTitle>
                <p className="text-xs text-slate-500">
                  Opening cash {formatCurrency(bridge.opening_cash)} → closing {formatCurrency(bridge.closing_cash)}
                </p>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={waterfallData} margin={{ top: 10, right: 10, left: 0, bottom: 40 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                    <XAxis dataKey="name" angle={-30} textAnchor="end" height={70} tick={{ fontSize: 11, fill: "#64748b" }} interval={0} />
                    <YAxis tickFormatter={formatCurrencyShort} tick={{ fontSize: 11, fill: "#64748b" }} width={60} />
                    <RechartsTooltip
                      cursor={{ fill: "#f8fafc" }}
                      formatter={(value, name, props) => {
                        const row = props.payload;
                        if (row.isTotal) return [formatCurrency(row.total), row.name];
                        return [`${row.delta >= 0 ? "+" : "−"}${formatCurrency(Math.abs(row.delta))}`, row.name];
                      }}
                    />
                    <Bar dataKey="base" stackId="w" fill="transparent" />
                    <Bar dataKey="value" stackId="w" radius={[4, 4, 0, 0]}>
                      {waterfallData.map((row, idx) => (
                        <Cell key={idx} fill={row.fill} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <div className="flex flex-wrap gap-4 mt-2 text-xs text-slate-500">
                  <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: "#10b981" }} /> Inflow</span>
                  <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: "#ef4444" }} /> Outflow</span>
                  <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: "#94a3b8" }} /> Internal &amp; other net</span>
                </div>
              </CardContent>
            </Card>

            {/* Runway projection */}
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold text-slate-900">Runway projection</CardTitle>
                <p className="text-xs text-slate-500">Cash drawn down at current burn (±20% band)</p>
              </CardHeader>
              <CardContent>
                {runwayProjection.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <AreaChart data={runwayProjection} margin={{ top: 10, right: 10, left: 0, bottom: 10 }}>
                      <defs>
                        <linearGradient id="runwayFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.3} />
                          <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                      <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={(m) => `${m}mo`} />
                      <YAxis tickFormatter={formatCurrencyShort} tick={{ fontSize: 11, fill: "#64748b" }} width={60} />
                      <RechartsTooltip
                        labelFormatter={(m) => `Month ${m}`}
                        formatter={(value, name) => [formatCurrency(value), name === "faster" ? "+20% burn" : name === "slower" ? "-20% burn" : "Expected"]}
                      />
                      <Area type="monotone" dataKey="slower" stroke="#93c5fd" strokeWidth={1} fill="transparent" strokeDasharray="4 3" />
                      <Area type="monotone" dataKey="faster" stroke="#93c5fd" strokeWidth={1} fill="transparent" strokeDasharray="4 3" />
                      <Area type="monotone" dataKey="base" stroke="#3b82f6" strokeWidth={2} fill="url(#runwayFill)" />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[300px] text-sm text-slate-400 text-center px-4">
                    No net burn to project - cash is stable or growing at the current rate.
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Monthly cash statement */}
          {monthlyStatement.length > 0 && (
            <Card className="border-0 shadow-sm mt-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold text-slate-900">Monthly cash statement</CardTitle>
                <p className="text-xs text-slate-500">
                  Inflows, outflows and running balance, month by month. Every column that
                  makes up Net is shown, so each row adds up: revenue + capital + subsidy
                  + other in + credits − operating − satellite − financing + internal = Net.
                </p>
              </CardHeader>
              <CardContent>
                <ScrollArea className="w-full">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Month</TableHead>
                        <TableHead className="text-right">Revenue</TableHead>
                        <TableHead className="text-right">Funding</TableHead>
                        <TableHead className="text-right">Subsidy</TableHead>
                        <TableHead className="text-right">Other in</TableHead>
                        <TableHead className="text-right">Credits</TableHead>
                        <TableHead className="text-right">Operating</TableHead>
                        <TableHead className="text-right">Satellite</TableHead>
                        <TableHead className="text-right">Financing</TableHead>
                        <TableHead className="text-right" title="Transfers between our own accounts whose two legs fall in different months">Internal</TableHead>
                        <TableHead className="text-right">Net</TableHead>
                        <TableHead className="text-right">Ending cash</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {monthlyStatement.map((row) => (
                        <TableRow key={row.month}>
                          <TableCell className="font-medium">{row.month}</TableCell>
                          <TableCell className="text-right text-emerald-600">{row.revenue ? formatCurrency(row.revenue) : "—"}</TableCell>
                          <TableCell className="text-right text-indigo-600">{row.capital ? formatCurrency(row.capital) : "—"}</TableCell>
                          <TableCell className="text-right text-teal-600">{row.subsidy ? formatCurrency(row.subsidy) : "—"}</TableCell>
                          <TableCell className="text-right text-slate-600">{row.other_inflow ? formatCurrency(row.other_inflow) : "—"}</TableCell>
                          <TableCell className="text-right text-emerald-600">{row.credits ? formatCurrency(row.credits) : "—"}</TableCell>
                          <TableCell className="text-right text-red-500">{row.operating_out ? `−${formatCurrency(row.operating_out)}` : "—"}</TableCell>
                          <TableCell className="text-right text-amber-600">{row.satellite_out ? `−${formatCurrency(row.satellite_out)}` : "—"}</TableCell>
                          <TableCell className="text-right text-rose-600">{row.financing_out ? `−${formatCurrency(row.financing_out)}` : "—"}</TableCell>
                          <TableCell className="text-right text-slate-500">
                            {row.internal_net ? `${row.internal_net >= 0 ? "+" : "−"}${formatCurrency(Math.abs(row.internal_net))}` : "—"}
                          </TableCell>
                          <TableCell className={`text-right font-medium ${row.net >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                            {row.net >= 0 ? "+" : "−"}{formatCurrency(Math.abs(row.net))}
                          </TableCell>
                          <TableCell className="text-right font-semibold text-slate-900">{formatCurrency(row.ending_cash)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Row 2: STRUCTURE */}
      <div>
        <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-3 flex items-center gap-2">
          <Building2 className="w-4 h-4 text-blue-500" />
          Cost Structure
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Spend by Category - Multi-view */}
          <Card className="border-0 shadow-sm lg:col-span-2">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-sm font-medium text-slate-700">Spend by Category</CardTitle>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs text-slate-500 hover:text-emerald-600"
                    onClick={() => navigate(withPeriod("/analytics/spend-by-category", selectedQuarter))}
                  >
                    View All <ExternalLink className="w-3 h-3 ml-1" />
                  </Button>
                </div>
                <div className="flex gap-1">
                  <Button
                    variant={categoryView === "donut" ? "default" : "ghost"}
                    size="sm"
                    className={`h-7 w-7 p-0 ${categoryView === "donut" ? "bg-emerald-600" : ""}`}
                    onClick={() => setCategoryView("donut")}
                  >
                    <PieChartIcon className="w-4 h-4" />
                  </Button>
                  <Button
                    variant={categoryView === "table" ? "default" : "ghost"}
                    size="sm"
                    className={`h-7 w-7 p-0 ${categoryView === "table" ? "bg-emerald-600" : ""}`}
                    onClick={() => setCategoryView("table")}
                  >
                    <TableIcon className="w-4 h-4" />
                  </Button>
                  <Button
                    variant={categoryView === "line" ? "default" : "ghost"}
                    size="sm"
                    className={`h-7 w-7 p-0 ${categoryView === "line" ? "bg-emerald-600" : ""}`}
                    onClick={() => setCategoryView("line")}
                  >
                    <LineChartIcon className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Donut View */}
              {categoryView === "donut" && (
                categoryData.length > 0 ? (
                  <div className="flex items-start gap-4">
                    <ResponsiveContainer width="50%" height={200}>
                      <PieChart>
                        <Pie
                          data={categoryData}
                          cx="50%"
                          cy="50%"
                          innerRadius={45}
                          outerRadius={75}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {categoryData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip 
                          formatter={(value) => formatCurrency(value)}
                          contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="flex-1 space-y-1.5 pt-2">
                      {categoryData.slice(0, 6).map((cat, idx) => (
                        <Tooltip key={idx}>
                          <TooltipTrigger asChild>
                            <div className="flex items-center justify-between text-sm cursor-default">
                              <div className="flex items-center gap-2 min-w-0 flex-1 mr-2">
                                <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ backgroundColor: cat.color }} />
                                <span className="text-slate-600 truncate">{cat.fullName || cat.name}</span>
                              </div>
                              <span className="font-medium text-slate-900 flex-shrink-0">{formatCurrencyShort(cat.value)}</span>
                            </div>
                          </TooltipTrigger>
                          <TooltipContent side="left" className="max-w-xs">
                            <p className="font-medium">{cat.fullName || cat.name}</p>
                            <p className="text-sm text-slate-500">{formatCurrency(cat.value)}</p>
                          </TooltipContent>
                        </Tooltip>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="h-[200px] flex items-center justify-center text-slate-400 text-sm">
                    No data
                  </div>
                )
              )}

              {/* Table View */}
              {categoryView === "table" && (
                <div className="max-h-[250px] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Category</TableHead>
                        <TableHead className="text-xs text-right">Amount</TableHead>
                        <TableHead className="text-xs text-right">% of Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {analytics?.spend_by_category?.map((cat, idx) => {
                        const total = analytics?.spend_by_category?.reduce((sum, c) => sum + c.amount, 0) || 1;
                        const pct = ((cat.amount / total) * 100).toFixed(1);
                        return (
                          <TableRow key={idx}>
                            <TableCell className="py-2">
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="flex items-center gap-2 cursor-default">
                                    <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                                    <span className="text-sm">{cat.category}</span>
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent side="right">
                                  <p>{cat.category}</p>
                                </TooltipContent>
                              </Tooltip>
                            </TableCell>
                            <TableCell className="py-2 text-right font-medium">{formatCurrency(cat.amount)}</TableCell>
                            <TableCell className="py-2 text-right text-slate-500">{pct}%</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Line Chart View - Monthly */}
              {categoryView === "line" && (
                analytics?.spend_by_category_monthly?.length > 0 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <LineChart data={analytics.spend_by_category_monthly}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="month" stroke="#64748b" fontSize={10} tickFormatter={formatMonthTick} />
                      <YAxis stroke="#64748b" fontSize={10} tickFormatter={formatCurrencyShort} />
                      <Tooltip 
                        formatter={(value) => formatCurrency(value)}
                        contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)', fontSize: '12px' }}
                      />
                      <Legend wrapperStyle={{ fontSize: '10px' }} />
                      {Object.keys(analytics.spend_by_category_monthly[0] || {})
                        .filter(k => k !== 'month')
                        .slice(0, 5)
                        .map((key, idx) => (
                          <Line 
                            key={key}
                            type="monotone"
                            dataKey={key}
                            stroke={COLORS[idx % COLORS.length]}
                            strokeWidth={2}
                            dot={{ r: 3 }}
                          />
                        ))
                      }
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-[220px] flex items-center justify-center text-slate-400 text-sm">
                    No monthly data available
                  </div>
                )
              )}
            </CardContent>
          </Card>

          {/* Fixed vs Variable */}
          <Card 
            className="border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => navigate(withPeriod("/analytics/fixed-variable", selectedQuarter))}
          >
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-slate-700">Fixed vs Variable</CardTitle>
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 pt-4">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-600">Fixed Costs</span>
                    <span className="font-semibold text-blue-600">{formatCurrency(analytics?.fixed_expenses)}</span>
                  </div>
                  <Progress value={fixedSharePct} className="h-2 bg-slate-100" />
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-slate-600">Variable Costs</span>
                    <span className="font-semibold text-violet-600">{formatCurrency(analytics?.variable_expenses)}</span>
                  </div>
                  <Progress value={variableSharePct} className="h-2 bg-slate-100 [&>div]:bg-violet-500" />
                </div>
                <div className="pt-2 border-t">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Fixed %</span>
                    <span className="font-medium">
                      {fixedSharePct.toFixed(0)}%
                    </span>
                  </div>
                  {fixedVariableUnattributed > 0 && (
                    <div className="flex justify-between text-sm mt-1">
                      <span className="text-slate-500">Unattributed (no counterparty, not split)</span>
                      <span className="font-medium text-slate-500">{formatCurrency(fixedVariableUnattributed)}</span>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Top Vendors */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-slate-700">Top Vendors</CardTitle>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowVendorList(!showVendorList)}
                    className="text-xs text-slate-500 hover:text-slate-700"
                  >
                    {showVendorList ? "Show Top 5" : `View All (${analytics?.vendor_count || 0})`}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate(withPeriod("/analytics/expenses", selectedQuarter))}
                    className="text-xs text-emerald-600 hover:text-emerald-700"
                  >
                    Full breakdown <ExternalLink className="w-3 h-3 ml-1" />
                  </Button>
                </div>
              </div>
              {(analytics?.intercompany_transfers?.amount || 0) > 0 && (
                <p className="text-xs text-slate-400">
                  Third parties only. {formatCurrencyShort(analytics.intercompany_transfers.amount)} to{" "}
                  {analytics.intercompany_transfers.name} ({analytics.intercompany_transfers.count}) shown separately.
                </p>
              )}
            </CardHeader>
            <CardContent>
              {showVendorList ? (
                <ScrollArea className="h-[240px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">#</TableHead>
                        <TableHead className="text-xs">Vendor</TableHead>
                        <TableHead className="text-xs text-right">Amount</TableHead>
                        <TableHead className="text-xs text-right">Txns</TableHead>
                        <TableHead className="text-xs text-right">%</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {analytics?.all_vendors?.map((vendor, idx) => (
                        <TableRow key={idx} className="text-xs">
                          <TableCell className="py-1.5">{idx + 1}</TableCell>
                          <TableCell className="py-1.5 max-w-[120px] truncate">{vendor.name}</TableCell>
                          <TableCell className="py-1.5 text-right font-medium text-red-500">
                            {formatCurrencyShort(vendor.amount)}
                          </TableCell>
                          <TableCell className="py-1.5 text-right text-slate-500">{vendor.count}</TableCell>
                          <TableCell className="py-1.5 text-right text-slate-500">{vendor.percentage}%</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              ) : (
                <div className="space-y-2">
                  {analytics?.top_vendors?.slice(0, 5).map((vendor, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded bg-slate-100 flex items-center justify-center text-xs font-medium text-slate-500">
                        {idx + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-700 truncate">{vendor.name}</p>
                        <div className="w-full bg-slate-100 rounded-full h-1 mt-0.5">
                          <div 
                            className="bg-red-400 h-1 rounded-full" 
                            style={{ width: `${vendor.percentage}%` }}
                          />
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-red-500">{formatCurrencyShort(vendor.amount)}</p>
                        <p className="text-xs text-slate-400">{vendor.percentage}% ({vendor.count})</p>
                      </div>
                    </div>
                  ))}
                  {(!analytics?.top_vendors || analytics.top_vendors.length === 0) && (
                    <div className="h-[160px] flex items-center justify-center text-slate-400 text-sm">
                      No vendor data
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Income Composition (where the money actually comes from) */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-slate-700">Income Composition</CardTitle>
              <p className="text-xs text-slate-400">
                Every inflow by economic source. Funding received is not revenue, and the borrowed part has to go back.
              </p>
            </CardHeader>
            <CardContent>
              {analytics?.income_breakdown?.length > 0 ? (
                <div className="space-y-2">
                  {(() => {
                    const total = analytics.income_breakdown.reduce((sum, g) => sum + g.amount, 0) || 1;
                    return analytics.income_breakdown.map((group, idx) => {
                      const pct = Math.round((group.amount / total) * 100);
                      const isRevenue = group.group === "revenue";
                      return (
                        <div key={idx} className="flex items-center gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-slate-700 truncate">
                              {INCOME_GROUP_LABELS[group.group] || group.group}
                            </p>
                            <div className="w-full bg-slate-100 rounded-full h-1 mt-0.5">
                              <div
                                className={`h-1 rounded-full ${isRevenue ? 'bg-emerald-400' : 'bg-blue-400'}`}
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          </div>
                          <div className="text-right">
                            <p className={`text-sm font-semibold ${isRevenue ? 'text-emerald-600' : 'text-slate-600'}`}>
                              {formatCurrencyShort(group.amount)}
                            </p>
                            <p className="text-xs text-slate-400">{pct}% ({group.count})</p>
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              ) : (
                <div className="h-[160px] flex items-center justify-center text-slate-400 text-sm">
                  No income data
                </div>
              )}
            </CardContent>
          </Card>

          {/* Income per Customer (real revenue only) */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium text-slate-700">Income per Customer</CardTitle>
                {(analytics?.customer_count || 0) > 5 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowCustomerList(!showCustomerList)}
                    className="text-xs text-slate-500 hover:text-slate-700"
                  >
                    {showCustomerList ? "Show Top 5" : `View All (${analytics?.customer_count || 0})`}
                  </Button>
                )}
              </div>
              <p className="text-xs text-slate-400">
                Real operating revenue only. Excludes funding received, refunds and transfers.
              </p>
            </CardHeader>
            <CardContent>
              {showCustomerList ? (
                <ScrollArea className="h-[240px]">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">#</TableHead>
                        <TableHead className="text-xs">Customer</TableHead>
                        <TableHead className="text-xs text-right">Amount</TableHead>
                        <TableHead className="text-xs text-right">Txns</TableHead>
                        <TableHead className="text-xs text-right">%</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {analytics?.income_by_customer?.map((customer, idx) => (
                        <TableRow key={idx} className="text-xs">
                          <TableCell className="py-1.5">{idx + 1}</TableCell>
                          <TableCell className="py-1.5 max-w-[120px] truncate">{customer.name}</TableCell>
                          <TableCell className="py-1.5 text-right font-medium text-emerald-600">
                            {formatCurrencyShort(customer.amount)}
                          </TableCell>
                          <TableCell className="py-1.5 text-right text-slate-500">{customer.count}</TableCell>
                          <TableCell className="py-1.5 text-right text-slate-500">{customer.percentage}%</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              ) : (
                <div className="space-y-2">
                  {analytics?.top_customers?.slice(0, 5).map((customer, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <div className="w-6 h-6 rounded bg-slate-100 flex items-center justify-center text-xs font-medium text-slate-500">
                        {idx + 1}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-700 truncate">{customer.name}</p>
                        <div className="w-full bg-slate-100 rounded-full h-1 mt-0.5">
                          <div
                            className="bg-emerald-400 h-1 rounded-full"
                            style={{ width: `${customer.percentage}%` }}
                          />
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-semibold text-emerald-600">{formatCurrencyShort(customer.amount)}</p>
                        <p className="text-xs text-slate-400">{customer.percentage}% ({customer.count})</p>
                      </div>
                    </div>
                  ))}
                  {(!analytics?.top_customers || analytics.top_customers.length === 0) && (
                    <div className="h-[160px] flex items-center justify-center text-slate-400 text-sm text-center px-4">
                      No operating revenue booked in this period
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Row 3: CONTROL */}
      <div>
        <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-3 flex items-center gap-2">
          <Target className="w-4 h-4 text-amber-500" />
          Operational Control
        </h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Missing Receipts</p>
                <FileText className="w-4 h-4 text-slate-400" />
              </div>
              <p className={`text-2xl font-bold ${analytics?.missing_receipts_pct > 15 ? 'text-red-500' : analytics?.missing_receipts_pct > 5 ? 'text-amber-500' : 'text-emerald-600'}`}>
                {analytics?.missing_receipts_pct}%
              </p>
              {/* Counted against the same population as the percentage above.
                  pending_expense_count includes charges no receipt can exist
                  for, so pairing the two reads as a different percentage. */}
              <p className="text-xs text-slate-500 mt-1">{analytics?.missing_receipts_count} of {analytics?.receiptable_expense_count ?? analytics?.pending_expense_count} pending</p>
              {analytics?.no_document_count > 0 && (
                <p className="text-xs text-slate-400 mt-0.5">{analytics.no_document_count} excluded, no document exists</p>
              )}
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Uncategorized</p>
                <BarChart3 className="w-4 h-4 text-slate-400" />
              </div>
              <p className={`text-2xl font-bold ${analytics?.uncategorized_pct > 10 ? 'text-red-500' : analytics?.uncategorized_pct > 5 ? 'text-amber-500' : 'text-emerald-600'}`}>
                {analytics?.uncategorized_pct}%
              </p>
              <p className="text-xs text-slate-500 mt-1">{analytics?.uncategorized_count} of {analytics?.pending_transaction_count} pending</p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Backlog</p>
                <Clock className="w-4 h-4 text-slate-400" />
              </div>
              <p className={`text-2xl font-bold ${analytics?.backlog_count > 50 ? 'text-red-500' : analytics?.backlog_count > 20 ? 'text-amber-500' : 'text-emerald-600'}`}>
                {analytics?.backlog_count}
              </p>
              <p className="text-xs text-slate-500 mt-1">pending review</p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Avg Processing</p>
                <Clock className="w-4 h-4 text-slate-400" />
              </div>
              <p className={`text-2xl font-bold ${analytics?.avg_processing_time > 14 ? 'text-red-500' : analytics?.avg_processing_time > 7 ? 'text-amber-500' : 'text-emerald-600'}`}>
                {analytics?.avg_processing_time} <span className="text-sm font-normal">days</span>
              </p>
              <p className="text-xs text-slate-500 mt-1">to completion</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Row 4: RISK */}
      <div>
        <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-3 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-red-500" />
          Risk Indicators
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">FX Exposure</p>
                <Globe className="w-4 h-4 text-slate-400" />
              </div>
              <p className={`text-2xl font-bold ${analytics?.fx_exposure_pct > 40 ? 'text-amber-500' : 'text-slate-700'}`}>
                {analytics?.fx_exposure_pct}%
              </p>
              <div className="mt-2 space-y-1">
                {Object.entries(analytics?.fx_exposure || {}).map(([currency, amount]) => (
                  <div key={currency} className="flex justify-between text-sm">
                    <span className="text-slate-500">{currency}</span>
                    <span className="font-medium">{formatCurrencyShort(amount)}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">High Risk Spend</p>
                <AlertTriangle className="w-4 h-4 text-slate-400" />
              </div>
              <p className="text-2xl font-bold text-red-500">
                {formatCurrency(analytics?.high_risk_spend)}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                {analytics?.high_risk_count} large transactions
              </p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Unassigned</p>
                <Users className="w-4 h-4 text-slate-400" />
              </div>
              <p className={`text-2xl font-bold ${analytics?.unassigned_pct > 30 ? 'text-amber-500' : 'text-slate-700'}`}>
                {analytics?.unassigned_pct}%
              </p>
              <p className="text-xs text-slate-500 mt-1">
                {analytics?.unassigned_count} transactions without owner
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Row 5: READINESS */}
      <div>
        <h2 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-3 flex items-center gap-2">
          <CheckCircle className="w-4 h-4 text-emerald-500" />
          Reporting Readiness
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          <Card className="border-0 shadow-sm lg:col-span-2">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Readiness Score</p>
                  <p className={`text-3xl font-bold mt-1 ${
                    analytics?.readiness_score >= 80 ? 'text-emerald-600' : 
                    analytics?.readiness_score >= 60 ? 'text-amber-500' : 'text-red-500'
                  }`}>
                    {analytics?.readiness_score}%
                  </p>
                </div>
                <div className={`p-4 rounded-xl ${
                  analytics?.readiness_score >= 80 ? 'bg-emerald-100' : 
                  analytics?.readiness_score >= 60 ? 'bg-amber-100' : 'bg-red-100'
                }`}>
                  <CheckCircle className={`w-8 h-8 ${
                    analytics?.readiness_score >= 80 ? 'text-emerald-600' : 
                    analytics?.readiness_score >= 60 ? 'text-amber-600' : 'text-red-600'
                  }`} />
                </div>
              </div>
              <Progress 
                value={analytics?.readiness_score || 0} 
                className={`h-3 ${
                  analytics?.readiness_score >= 80 ? '[&>div]:bg-emerald-500' : 
                  analytics?.readiness_score >= 60 ? '[&>div]:bg-amber-500' : '[&>div]:bg-red-500'
                }`}
              />
              <p className="text-xs text-slate-500 mt-2">
                {analytics?.readiness_score >= 80 ? 'Excellent - Ready for audit/reporting' : 
                 analytics?.readiness_score >= 60 ? 'Good - Minor improvements needed' : 
                 'Needs attention - Review categorization & receipts'}
              </p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Payroll Ratio</p>
                <Users className="w-4 h-4 text-slate-400" />
              </div>
              <p className="text-2xl font-bold text-slate-700">
                {analytics?.payroll_ratio}%
              </p>
              <p className="text-xs text-slate-500 mt-1">of total expenses</p>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Cost/Revenue</p>
                <DollarSign className="w-4 h-4 text-slate-400" />
              </div>
              <p className={`text-2xl font-bold ${analytics?.cost_per_revenue == null ? 'text-slate-400' : analytics?.cost_per_revenue > 100 ? 'text-red-500' : 'text-emerald-600'}`}>
                {analytics?.cost_per_revenue == null ? 'n/a' : `${analytics.cost_per_revenue}%`}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                {analytics?.cost_per_revenue == null
                  ? 'No operating revenue booked'
                  : analytics?.cost_per_revenue > 100 ? 'Spending exceeds revenue' : 'Healthy ratio'}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Monthly Trend Chart */}
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg">Monthly Trend</CardTitle>
        </CardHeader>
        <CardContent>
          {trendData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={trendData}>
                <defs>
                  <linearGradient id="incomeGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                  </linearGradient>
                  <linearGradient id="expenseGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="shortMonth" stroke="#64748b" fontSize={11} angle={-45} textAnchor="end" height={50} />
                <YAxis stroke="#64748b" fontSize={12} tickFormatter={formatCurrencyShort} />
                <RechartsTooltip 
                  formatter={(value, name) => [formatCurrency(value), name]}
                  labelFormatter={(label) => label}
                  contentStyle={{ 
                    borderRadius: '8px', 
                    border: 'none', 
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
                    backgroundColor: 'white',
                    padding: '12px'
                  }}
                  cursor={{ stroke: '#94a3b8', strokeWidth: 1 }}
                />
                <Area type="monotone" dataKey="income" stroke="#10b981" fill="url(#incomeGradient)" strokeWidth={2} name="Income" />
                <Area type="monotone" dataKey="expense" stroke="#ef4444" fill="url(#expenseGradient)" strokeWidth={2} name="Expenses" />
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-slate-500">
              No trend data available
            </div>
          )}
        </CardContent>
      </Card>

      {/* Footer Summary */}
      <div className="text-center py-4 border-t">
        <p className="text-sm text-slate-500">
          Last updated: {new Date().toLocaleString()} · {analytics?.transaction_count || 0} transactions analyzed
        </p>
      </div>
    </div>
    </TooltipProvider>
  );
}

export default AdminDashboardPage;
